import { useState, useRef, useEffect, useCallback } from "react";
import {
  INTERSECTION_THRESHOLD,
  ROOT_MARGIN,
} from "../modules/CoursesConstants";

/**
 * Tracks visibility of course DOM elements using IntersectionObserver.
 * Maintains a set of currently visible course IDs.
 *
 * @param {boolean} viewOnly - If true, disables the observer
 * @returns {{ visibleCourseIds: string[], registerCourseRef: Function }}
 */
const useVisibleEntities = viewOnly => {
  const [visibleCourseIds, setVisibleCourseIds] = useState([]);
  const coursesRef = useRef({});
  const observerRef = useRef(null);

  const observeElement = useCallback((element, observer) => {
    if (element && element.nodeType === Node.ELEMENT_NODE && observer) {
      observer.observe(element);
    }
  }, []);

  const unobserveElement = useCallback((element, observer) => {
    if (element && element.nodeType === Node.ELEMENT_NODE && observer) {
      observer.unobserve(element);
    }
  }, []);

  useEffect(() => {
    if (viewOnly) return;

    observerRef.current = new IntersectionObserver(
      entries => {
        const visibleIds = [];
        const hiddenIds = [];

        entries.forEach(entry => {
          const elementId = entry.target.getAttribute("data-id");
          if (elementId) {
            if (entry.isIntersecting) {
              visibleIds.push(elementId);
            } else {
              hiddenIds.push(elementId);
            }
          }
        });

        if (visibleIds.length > 0 || hiddenIds.length > 0) {
          setVisibleCourseIds(prevIds => {
            const updatedIds = new Set(prevIds);
            visibleIds.forEach(id => updatedIds.add(id));
            hiddenIds.forEach(id => updatedIds.delete(id));
            return Array.from(updatedIds);
          });
        }
      },
      {
        threshold: INTERSECTION_THRESHOLD,
        rootMargin: ROOT_MARGIN,
      }
    );

    // Observe elements that registered before the observer was ready
    Object.values(coursesRef.current).forEach(element => {
      observeElement(element, observerRef.current);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [viewOnly, observeElement]);

  const registerCourseRef = useCallback(
    (id, element) => {
      if (element) {
        coursesRef.current[id] = element;
        if (observerRef.current) {
          observeElement(element, observerRef.current);
        }
      } else {
        const oldElement = coursesRef.current[id];
        if (oldElement && observerRef.current) {
          unobserveElement(oldElement, observerRef.current);
        }
        delete coursesRef.current[id];
      }
    },
    [observeElement, unobserveElement]
  );

  return {
    visibleCourseIds,
    registerCourseRef,
  };
};

export default useVisibleEntities;
